import {Config as BaseGrpcConfig} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import type {Transport} from 'protoc-gen-ts';
import React, {FC, useCallback, useContext, useMemo, useRef} from 'react';
import {MarkOptional} from 'ts-essentials';
import type {OnTrailersCallback} from './GrpcClientChannel';
import {RefetchRequestsWithPendingAbTestActivations} from './RefetchRequestsWithPendingAbTestActivations';
import {DeadlineExceededTimeout} from './appendGrpcCircuitBreaker';
import {GrpcTrailersConfig} from './useGenericGrpcRequest';

export type GrpcConfig = MarkOptional<BaseGrpcConfig, 'traceId'>;
export type LegacyGrpcConfig = {
  shopId: number;
  childShopId: number;
  device: 'mobile' | 'desktop';
};

interface GrpcClientSettings {
  debug?: boolean;
  hostname: string;
  legacyHostname: string;
  credentials?: null | {[index: string]: string};
  options?: null | {[index: string]: string};
  timeout?: DeadlineExceededTimeout;
}

export interface GrpcContextProps {
  config: GrpcConfig;
  legacyConfig: LegacyGrpcConfig;
  settings: GrpcClientSettings;
  nonCriticalErrorMessagePatterns?: RegExp[];
  abTestOptions?: {
    abTestHeaderName?: string;
  };
  updateAbTestConfig?: (abTestConfig: GrpcConfig['abTests']) => void;
  updateAbTestIdentifier?: (abTestIdentifier: string) => void;
}

interface RequestGrpcClientChannelConfig {
  legacy?: boolean;
}
interface GrpcContext {
  config: GrpcConfig;
  legacyConfig: LegacyGrpcConfig;
  requestGrpcClientChannel: (
    config: RequestGrpcClientChannelConfig,
  ) => Promise<Transport>;
  nonCriticalErrorMessagePatterns?: RegExp[];
  abTestHeaderName?: string;
  getAbTestList: () => string[];
  setAbTestList: (newAbTestList: string[]) => void;
  updateAbTestConfig?: (abTestConfig: GrpcConfig['abTests']) => void;
  updateAbTestIdentifier?: (abTestIdentifier: string) => void;
}

const ctx = React.createContext<GrpcContext>({
  get config(): GrpcConfig {
    throw new Error(
      'Missing GRPC context value. Please use the GrpcClientProvider',
    );
  },
  get legacyConfig(): LegacyGrpcConfig {
    throw new Error(
      'Missing GRPC context value. Please use the GrpcClientProvider',
    );
  },
  requestGrpcClientChannel: async () => {
    throw new Error(
      'Missing GRPC context value. Please use the GrpcClientProvider',
    );
  },
  getAbTestList: () => [],
  setAbTestList: () => {},
});

export const useGrpcClientConfig = () => {
  return useContext(ctx).config;
};

export const useLegacyGrpcClientConfig = () => useContext(ctx).legacyConfig;
export const useRequestGrpcClientChannel = () =>
  useContext(ctx).requestGrpcClientChannel;
export const useRequestNonCriticalErrorMessagePatterns = () =>
  useContext(ctx).nonCriticalErrorMessagePatterns;
const useGetAbTestHeaderName = () => useContext(ctx).abTestHeaderName;
export const useAbTestsFromTrailers = () => useContext(ctx).getAbTestList();
export const useSetAbTestsFromTrailers = () => useContext(ctx).setAbTestList;
export const useUpdateAbTestConfig = () => useContext(ctx).updateAbTestConfig;
export const useUpdateAbTestIdentifier = () =>
  useContext(ctx).updateAbTestIdentifier;
/**
 * On the server this is the `X-AB-Test-User-Id` header, on the client the `ay-ab-test-user-id`
 * cookie read during hydration. It stays empty until the user accepts the consent banner.
 */
export const useAbTestIdentifier = () =>
  useContext(ctx).config.abTestIdentifier;

export const GrpcClientProvider: FC<
  React.PropsWithChildren<GrpcContextProps>
> = ({
  children,
  config,
  legacyConfig,
  settings,
  nonCriticalErrorMessagePatterns,
  abTestOptions,
  updateAbTestConfig,
  updateAbTestIdentifier,
}) => {
  const abTestList = useRef<string[]>([]);
  const getAbTestList = useCallback(() => {
    return abTestList.current;
  }, [abTestList.current]);

  const setAbTestList = useCallback((newAbTestList: string[]) => {
    abTestList.current = [
      ...new Set([...abTestList.current, ...newAbTestList]),
    ];
  }, []);

  /**
   * Stream requests, unlike unary requests doesn't have the trailers in the initial response data.
   * Instead we need to listen for the trailers using the onTrailers callback from the GRPC Transport class
   * This callback is passed to the GrpcClientChannel class and is called only on stream requests.
   */
  const onStreamTrailers = useCallback(
    (trailers: GrpcTrailersConfig['trailers']) => {
      if (
        abTestOptions?.abTestHeaderName &&
        trailers?.[abTestOptions?.abTestHeaderName]
      ) {
        setAbTestList(trailers[abTestOptions?.abTestHeaderName] as string[]);
      }
    },
    [abTestOptions?.abTestHeaderName, setAbTestList],
  );

  const requestGrpcClientChannel = useCallback(
    async (config: RequestGrpcClientChannelConfig) => {
      return createGrpcClientChannel(config, settings, onStreamTrailers);
    },
    [onStreamTrailers, settings],
  );

  const value = useMemo(
    () => ({
      config,
      legacyConfig,
      requestGrpcClientChannel,
      nonCriticalErrorMessagePatterns,
      abTestHeaderName: abTestOptions?.abTestHeaderName,
      getAbTestList,
      setAbTestList,
      updateAbTestConfig,
      updateAbTestIdentifier,
    }),
    [
      config,
      legacyConfig,
      requestGrpcClientChannel,
      nonCriticalErrorMessagePatterns,
      abTestOptions?.abTestHeaderName,
      getAbTestList,
      setAbTestList,
      updateAbTestConfig,
      updateAbTestIdentifier,
    ],
  );

  return (
    <ctx.Provider value={value}>
      <RefetchRequestsWithPendingAbTestActivations>
        {children}
      </RefetchRequestsWithPendingAbTestActivations>
    </ctx.Provider>
  );
};

export async function createGrpcClientChannel(
  config: RequestGrpcClientChannelConfig,
  settings: GrpcClientSettings,
  onStreamTrailers?: OnTrailersCallback,
) {
  const {hostname, legacyHostname, debug, timeout} = settings;
  const {TransportWithTimeout, getTransport} = await import(
    './GrpcClientChannel'
  );
  const {GrpcWebTransport, Configure} = await import('protoc-gen-ts');

  let channel: Transport = new GrpcWebTransport(
    config.legacy ? legacyHostname : hostname,
    {
      transport: await getTransport(),
      debug,
    },
  );

  channel = new TransportWithTimeout(channel, timeout, onStreamTrailers);

  // Need to call Configure before using the channel once for the app
  Configure();

  return channel;
}

export const useGetConsideredAbTestsFromGrpcTrailers = () => {
  const abTestHeaderName = useGetAbTestHeaderName();

  return (grpcTrailers?: GrpcTrailersConfig['trailers']) => {
    if (grpcTrailers && abTestHeaderName && abTestHeaderName in grpcTrailers) {
      return grpcTrailers[abTestHeaderName] as string[];
    }

    return [];
  };
};
